import { useLingui } from "@lingui/react/macro";
import { Button } from "@rakazo/ui-web";
import { Bell, PanelLeftClose, PanelLeftOpen, Plus, Search } from "lucide-react";
import { IconTooltip } from "../../components/IconTooltip";
import { desktopBridge } from "../../lib/desktop";

export function SidebarHeader({
  mini = false,
  activityMode,
  toggleActivityMode,
  searchOpen = false,
  onSearch,
  setBotsSidebarCollapsedPref,
  setMobileSidebarOpen,
  setCreateTeamOpen,
}: {
  mini?: boolean;
  activityMode: boolean;
  toggleActivityMode: () => void;
  searchOpen?: boolean;
  onSearch?: () => void;
  setBotsSidebarCollapsedPref: (collapsed: boolean) => void;
  setMobileSidebarOpen: (open: boolean) => void;
  setCreateTeamOpen: (open: boolean) => void;
}) {
  const { t } = useLingui();
  // macOS keeps its native traffic lights at the window's top-left, over this row.
  const clearsMacTrafficLights = desktopBridge()?.platform === "darwin";
  const toggleLabel = mini ? t`Show bots` : t`Minimize bots`;
  const iconClass =
    "app-no-drag size-7 rounded-md text-foreground-subtle hover:bg-hover hover:text-foreground";
  return (
    <div
      className={`app-drag flex shrink-0 items-center ${
        mini ? "flex-col gap-1 px-0 py-2" : "h-12 justify-between gap-1 px-2"
      }`}
    >
      {!mini ? (
        <span
          className={`min-w-0 truncate px-1.5 text-ui-base font-medium text-foreground-subtle ${
            clearsMacTrafficLights ? "ms-[64px]" : ""
          }`}
        >
          {t`Companies`}
        </span>
      ) : null}
      <div className={`relative flex items-center ${mini ? "flex-col gap-1" : "gap-0.5"}`}>
        {onSearch ? (
          <IconTooltip label={t`Search`}>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t`Search`}
              aria-pressed={searchOpen}
              data-testid="sidebar-search-trigger"
              onClick={onSearch}
              className={iconClass}
            >
              <Search className="size-3.5" strokeWidth={1.8} aria-hidden="true" />
            </Button>
          </IconTooltip>
        ) : null}
        <IconTooltip label={t`Activity`}>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t`Activity`}
            aria-pressed={activityMode}
            data-activity-mode={activityMode ? "on" : "off"}
            onClick={toggleActivityMode}
            className={`${iconClass} ${activityMode ? "bg-selected text-foreground" : ""}`}
          >
            <Bell
              className="size-3.5"
              strokeWidth={1.8}
              fill={activityMode ? "currentColor" : "none"}
              aria-hidden="true"
            />
          </Button>
        </IconTooltip>
        <IconTooltip label={toggleLabel}>
          <Button
            variant="ghost"
            size="icon-sm"
            className={`${iconClass} hidden md:inline-flex`}
            aria-label={toggleLabel}
            data-testid={mini ? "restore-bots-sidebar" : "minimize-bots-sidebar"}
            onClick={() => setBotsSidebarCollapsedPref(!mini)}
          >
            {mini ? (
              <PanelLeftOpen className="size-3.5" strokeWidth={1.8} aria-hidden="true" />
            ) : (
              <PanelLeftClose className="size-3.5" strokeWidth={1.8} aria-hidden="true" />
            )}
          </Button>
        </IconTooltip>
        <IconTooltip label={t`New company`}>
          <Button
            variant="ghost"
            size="icon-sm"
            className={iconClass}
            aria-label={t`New company`}
            data-testid="create-team-trigger"
            onClick={() => {
              setMobileSidebarOpen(false);
              setCreateTeamOpen(true);
            }}
          >
            <Plus className="size-3.5" strokeWidth={1.8} aria-hidden="true" />
          </Button>
        </IconTooltip>
      </div>
    </div>
  );
}
